import React, { useEffect, useState, useMemo } from 'react';
import { API_URL } from '../config';
import { Feedback } from '../types/feedback';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import '../utils/pdfWorker';

import { useAppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { ApplicationData } from '../pages/Landing';

interface ApplicationPreviewProps {
  application_data: ApplicationData | null;
}

const styles = {
  container: {
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    backgroundColor: 'white',
    minHeight: '200px',
    maxHeight: '600px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative' as const,
  },
  pdfContainer: {
    position: 'relative' as const,
    width: '100%',
    height: '500px',
    backgroundColor: '#f7fafc',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  textContainer: {
    padding: '16px 32px',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid #e2e8f0',
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
  },
  companyName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2D3748',
    margin: 0,
  },
  status: {
    fontSize: '14px',
    color: '#718096',
    marginTop: '8px',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  downloadButton: {
    backgroundColor: '#4A5568',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    marginTop: '12px',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#2D3748',
    },
  },
  pdfDocument: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  pdfPage: {
    maxWidth: '95% !important',
    maxHeight: '100% !important',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  
  /* Make sure the page canvas fills the container */
  pdfPageCanvas: {
    width: '100% !important',
    height: 'auto !important',
    maxHeight: '100% !important',
  },
  continueButton: {
    backgroundColor: 'white',
    color: 'black',
    padding: '10px 20px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#f8f8f8',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    },
  }
};

const ApplicationPreview: React.FC<ApplicationPreviewProps> = ({ application_data }) => {   
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const { setApplicationId, setDeckFeedback, setAnalysisData, setUploadedFile } = useAppContext();
  const navigate = useNavigate();

  // Set loading to false when application_data is available
  useEffect(() => {
    if (application_data) {
      setLoading(false);
    }
  }, [application_data]);

  // Create PDF URL from base64 data
  const pdfUrl = useMemo(() => {
    if (!application_data?.pdf_data) {
      console.log('No PDF data available');
      return null;
    }
    try {
      console.log('Attempting to create PDF URL');
      const binaryString = window.atob(application_data.pdf_data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      console.log('PDF URL created successfully');
      return url;
    } catch (err) {
      console.error('Error creating PDF URL:', err);
      setError('Failed to load PDF file');
      return null;
    }
  }, [application_data?.pdf_data]);

  // Cleanup URL on unmount
  useEffect(() => {
    if (pdfUrl) {
      console.log('PDF URL is available:', pdfUrl);
    }
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return <div style={styles.container}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.pdfContainer}>
        {pdfUrl ? (
          <Document
            file={pdfUrl}
            loading={<div>Loading PDF...</div>}
            error={<div>Error loading PDF. Please try again.</div>}
            className="pdf-document"
            onLoadError={(error: Error) => {
              console.error('Document load error:', error);
              setError('Failed to load PDF document');
            }}
          >
            <Page
              pageNumber={1}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="pdf-page"
              onLoadError={(error: Error) => {
                console.error('Page load error:', error);
                setError('Failed to load PDF page');
              }}
            />
          </Document>
        ) : (
          <div>No PDF available</div>
        )}
      </div>
      <div style={styles.textContainer}>
        <div style={styles.infoContainer}>
          <h3 style={styles.companyName}>
            {application_data?.company_name}
          </h3>
          {application_data?.status && (
            <p style={styles.status}>Status: {application_data.status}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationPreview;
